import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ShieldCheck, X, Info } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { openOneTimeCheckoutGuest } from '../../lib/billing';

/**
 * Guest checkout dialog for the Profile Success one-time plan.
 *
 * Replaces the Razorpay *hosted Payment Button* iframe (whose modal has no
 * visible close X) with a Shadcn Dialog that:
 *   - Has a proper X in the top-right + Cancel button + click-outside dismiss.
 *   - Collects email / name / phone upfront (same as the hosted button did).
 *   - Then invokes Razorpay's SDK Checkout (which reliably has its own X
 *     close button on the payment sheet).
 *
 * Post-payment: the ``payment.captured`` webhook provisions the account by
 * email; we surface a "check your inbox" toast + redirect the buyer to
 * ``/login`` where they can sign in with the credentials mailed to them.
 */
export function GuestCheckoutDialog({
  open,
  onOpenChange,
  planLabel = 'Full Profile Makeover',
  priceLabel = '₹999',
  triggerTestId = 'guest-checkout-dialog',
}) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});

  const reset = () => {
    setEmail('');
    setName('');
    setPhone('');
    setBusy(false);
    setErrors({});
  };

  const validate = () => {
    const e = {};
    const emailTrim = email.trim();
    if (!emailTrim || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      e.email = 'Enter a valid email address';
    }
    const nameTrim = name.trim();
    if (!nameTrim || nameTrim.length < 2) {
      e.name = 'Enter your full name';
    }
    const phoneTrim = phone.trim();
    if (!phoneTrim || phoneTrim.replace(/\D/g, '').length < 8) {
      e.phone = 'Enter a valid phone number';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleProceed = async () => {
    if (!validate()) return;
    setBusy(true);
    const emailForCheckout = email.trim();
    try {
      await openOneTimeCheckoutGuest({
        email: emailForCheckout,
        name: name.trim(),
        phone: phone.trim(),
        onSuccess: (result) => {
          // Close our dialog and route to the region-specific thank-you
          // page. Region is inferred from the purchase currency so GA4
          // URL-based conversion goals can segment US vs India. From the
          // thank-you page we bounce the buyer to /login with their
          // email pre-filled + a "you just paid" banner — this is the
          // single most effective guard against the duplicate-account
          // bug (the buyer literally can't retype a different email).
          onOpenChange?.(false);
          reset();
          const cur = String(result?.currency || 'USD').toUpperCase();
          const region = cur === 'INR' ? 'in' : 'us';
          const amt = result?.amount_paise ? (Number(result.amount_paise) / 100) : undefined;
          const params = new URLSearchParams();
          if (result?.plan) params.set('plan', result.plan);
          if (cur) params.set('currency', cur);
          if (amt != null) params.set('amount', String(amt));
          if (result?.order_id) params.set('order_id', result.order_id);
          if (result?.payment_id) params.set('payment_id', result.payment_id);
          params.set('email', emailForCheckout);
          setTimeout(() => {
            navigate(`/thank-you/${region}?${params.toString()}`);
          }, 400);
        },
        onDismiss: () => {
          // User closed the Razorpay modal without paying — leave our
          // dialog open so they can retry or adjust info.
          setBusy(false);
        },
      });
    } catch (_e) {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (busy && v === false) return; // don't allow close mid-request
        onOpenChange?.(v);
        if (!v) reset();
      }}
    >
      <DialogContent
        className="sm:max-w-md"
        data-testid={triggerTestId}
      >
        {/* Explicit close X — belt-and-braces on top of Radix's default close.
            Radix already renders an X in the top-right of DialogContent, but
            we surface a second visible affordance for zero-doubt UX. */}
        <DialogClose
          className="absolute right-4 top-4 rounded-full p-1.5 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
          data-testid="guest-checkout-close"
          aria-label="Close checkout"
        >
          <X className="h-4 w-4" />
        </DialogClose>

        <DialogHeader className="text-left pr-8">
          <DialogTitle className="text-2xl font-display font-semibold tracking-tight">
            {planLabel}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Enter your details to continue to secure payment via Razorpay.
            You&apos;ll receive login credentials by email once payment is confirmed.
          </DialogDescription>
        </DialogHeader>

        {/* Same-email nudge — critical because the webhook auto-provisions
            the Blayzrr account using the email entered *here*. If a buyer
            later signs up with a DIFFERENT email, they get a second (unpaid)
            account and support has to manually reconcile. Making this
            impossible-to-miss cuts a huge chunk of support tickets. */}
        <div
          className="mt-4 rounded-lg border border-primary/30 bg-primary/5 p-3 flex items-start gap-2.5"
          data-testid="guest-checkout-email-notice"
        >
          <Info size={16} className="text-primary shrink-0 mt-0.5" aria-hidden="true" />
          <div className="text-[12px] leading-relaxed text-foreground/90">
            <span className="font-semibold text-primary">Important:</span> Use an email
            you&apos;ll remember and check. This is the email
            you&apos;ll use to <span className="font-semibold">log in</span> — and where
            we&apos;ll send your login credentials right after payment.
            <span className="block mt-1 text-muted-foreground">
              Please use the <span className="font-semibold text-foreground">same email at signup / login</span> to avoid creating a duplicate account.
            </span>
          </div>
        </div>

        <div className="space-y-4 mt-4">
          <div className="space-y-1.5">
            <Label htmlFor="guest-checkout-email" className="text-xs font-medium">
              Email <span className="text-primary">*</span>
            </Label>
            <Input
              id="guest-checkout-email"
              type="email"
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
              placeholder="you@company.com"
              autoComplete="email"
              disabled={busy}
              data-testid="guest-checkout-email-input"
              className={errors.email ? 'border-destructive focus-visible:ring-destructive' : ''}
            />
            {errors.email && (
              <p className="text-[11px] text-destructive">{errors.email}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="guest-checkout-name" className="text-xs font-medium">
              Name <span className="text-primary">*</span>
            </Label>
            <Input
              id="guest-checkout-name"
              value={name}
              onChange={(ev) => setName(ev.target.value)}
              placeholder="Your full name"
              autoComplete="name"
              disabled={busy}
              data-testid="guest-checkout-name-input"
              className={errors.name ? 'border-destructive focus-visible:ring-destructive' : ''}
            />
            {errors.name && (
              <p className="text-[11px] text-destructive">{errors.name}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="guest-checkout-phone" className="text-xs font-medium">
              Phone <span className="text-primary">*</span>
            </Label>
            <Input
              id="guest-checkout-phone"
              type="tel"
              value={phone}
              onChange={(ev) => setPhone(ev.target.value)}
              placeholder="+91 98765 43210"
              autoComplete="tel"
              disabled={busy}
              data-testid="guest-checkout-phone-input"
              className={errors.phone ? 'border-destructive focus-visible:ring-destructive' : ''}
            />
            {errors.phone && (
              <p className="text-[11px] text-destructive">{errors.phone}</p>
            )}
          </div>
        </div>

        {/* Sticky footer: price + primary CTA + explicit cancel */}
        <div className="mt-6 flex items-center justify-between gap-3 pt-4 border-t border-border">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
              You pay
            </div>
            <div className="text-2xl font-display font-semibold tabular-nums">
              {priceLabel}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DialogClose asChild>
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                data-testid="guest-checkout-cancel"
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              size="lg"
              onClick={handleProceed}
              disabled={busy}
              data-testid="guest-checkout-proceed"
              className="rounded-full min-w-[160px]"
            >
              {busy ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Opening…
                </>
              ) : (
                <>Buy Now</>
              )}
            </Button>
          </div>
        </div>

        <p className="mt-3 flex items-center justify-center gap-2 text-[11px] text-muted-foreground">
          <ShieldCheck size={11} className="text-primary/70" aria-hidden="true" />
          Razorpay secure · UPI, Cards, Netbanking supported
        </p>
      </DialogContent>
    </Dialog>
  );
}

export default GuestCheckoutDialog;
